// The one place a failed API call becomes the sentence the user reads.
//
// Pure — no axios, no window — so the runner can prove it without a browser.
// api-client.js re-exports it; every surface imports it from there (or from
// the shell's services/api) and never re-derives the message itself.
//
// Two error shapes reach the frontend and BOTH must surface their reasons:
//
//   1. The Jira-proxy shape, reasons nested under `details`:
//        { error, details: { errorMessages: [...], errors: {...}, message } }
//   2. The Stride engine shape, reasons at the TOP level (what the transition
//      handler returns when a validator or condition blocks a transition, and
//      what the issue-security preflight returns):
//        { error: 'Transition blocked', errorMessages: [...], errors: {...} }
//
// The second was invisible until 2026-09-11: only `details.*` was read, so a
// blocked transition showed the bare "Transition blocked" and never the
// validator that blocked it.
export function extractErrorMessage(err, fallback = 'An error occurred') {
  const data = err?.response?.data;
  if (!data || typeof data !== 'object') return err?.message || fallback;

  const reasonsOf = (bag) => {
    if (!bag || typeof bag !== 'object') return null;
    if (Array.isArray(bag.errorMessages)) {
      const msgs = bag.errorMessages.filter(Boolean);
      if (msgs.length > 0) return msgs.join('. ');
    }
    if (bag.errors && typeof bag.errors === 'object') {
      const fieldErrors = Object.values(bag.errors).filter(Boolean);
      if (fieldErrors.length > 0) return fieldErrors.join('. ');
    }
    return null;
  };

  const nested = reasonsOf(data.details);
  if (nested) return nested;
  if (data.details?.message) return data.details.message;

  const topLevel = reasonsOf(data);
  if (topLevel) return topLevel;

  if (data.error && data.error !== fallback) return data.error;
  if (data.message) return data.message;

  return err?.message || fallback;
}
